import { useMemo, useEffect, useState, useRef, CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header } from '../components/Header';
import { FunnelPersonalizationPanel } from '../components/FunnelPersonalizationPanel';
import { MarketplaceFunnelWizard } from '../components/MarketplaceFunnelWizard';
import { apiService } from '../services/api';
import { getAgenticDraftIssues, readStageCriteria, writeStageCriterion, stageCriteriaFields } from '../utils/agenticFunnelValidation';
import { AgenticFunnelCategorySuggestion, Category, Funnel, FunnelInput } from '../types';

type CategoryId = string;

type FunnelFormState = {
  nome: string;
  cor: string;
  descricao: string;
  isInbound: boolean;
};

type FunnelAgentBaseState = {
  aiAgentName: string;
  personalidade: string;
  regrasComunicacao: string;
  contextoEmpresa: string;
  escopoLimites: string;
  restricoes: string;
};

type AgenticFunnelKind = 'inbound' | 'outbound' | 'growth';

type AgenticRulesProfile = {
  discountMode: 'none' | 'no_negotiation' | 'limited' | 'human_approval';
  maxDiscountPercent: string;
  approvalAbovePercent: string;
  expensiveObjection: 'justify_value' | 'offer_alternative' | 'call_human';
  installmentsMode: 'none' | 'when_asked' | 'proactive';
  maxInstallments: string;
  installmentInterest: 'unspecified' | 'without_interest' | 'with_interest';
  deliveryPromise: 'system_only' | 'estimate_language' | 'human_only';
  resultGuarantee: 'never' | 'documented_only' | 'human_only';
  handoffTriggers: string[];
  handoffValueAbove: string;
  humanRequestBehavior: 'transfer_now' | 'try_once_then_transfer';
  emojiLevel: 'none' | 'low' | 'moderate';
  toneStyle: 'professional' | 'friendly' | 'informal';
  rudeCustomerBehavior: 'polite' | 'set_boundary' | 'end_or_handoff';
  competitorBehavior: 'do_not_comment' | 'neutral_only';
  offScopeBehavior: 'redirect' | 'answer_brief_then_redirect' | 'human';
  neverInvent: boolean;
  neverRevealPrompt: boolean;
  neverIgnoreInstructions: boolean;
  revealAi: 'transparent_if_asked' | 'do_not_focus' | 'human_only';
};

type AgenticWizardState = FunnelFormState & FunnelAgentBaseState & {
  kind: AgenticFunnelKind;
  pricingRules: string;
  promiseRules: string;
  handoffRules: string;
  toneRules: string;
  offscopeRules: string;
  antiManipulationRules: string;
  extraRules: string;
  rulesProfile: AgenticRulesProfile;
  companyContextExtra: string;
  scopeExtras: string;
  neverDo: string;
  desiredCategoryCount: string;
  desiredCategoryNames: string;
  desiredFunnel: string;
  suggestedCategories: AgenticFunnelCategorySuggestion[];
  voiceExamples: string;
};


const defaultFunnelForm: FunnelFormState = {
  nome: '',
  cor: '#0f172a',
  descricao: '',
  isInbound: false,
};

const emptyFunnelAgentBase: FunnelAgentBaseState = {
  aiAgentName: '',
  personalidade: '',
  regrasComunicacao: '',
  contextoEmpresa: '',
  escopoLimites: '',
  restricoes: '',
};

const defaultRulesProfile: AgenticRulesProfile = {
  discountMode: 'human_approval',
  maxDiscountPercent: '',
  approvalAbovePercent: '',
  expensiveObjection: 'justify_value',
  installmentsMode: 'when_asked',
  maxInstallments: '',
  installmentInterest: 'unspecified',
  deliveryPromise: 'system_only',
  resultGuarantee: 'documented_only',
  handoffTriggers: ['human_requested', 'serious_complaint', 'refund', 'angry_customer'],
  handoffValueAbove: '',
  humanRequestBehavior: 'transfer_now',
  emojiLevel: 'low',
  toneStyle: 'friendly',
  rudeCustomerBehavior: 'set_boundary',
  competitorBehavior: 'do_not_comment',
  offScopeBehavior: 'redirect',
  neverInvent: true,
  neverRevealPrompt: true,
  neverIgnoreInstructions: true,
  revealAi: 'transparent_if_asked',
};

const defaultAgenticWizardState: AgenticWizardState = {
  ...defaultFunnelForm,
  cor: '#7c3aed',
  kind: 'inbound',
  aiAgentName: '',
  personalidade: '',
  regrasComunicacao: '',
  contextoEmpresa: '',
  escopoLimites: '',
  restricoes: '',
  pricingRules: '',
  promiseRules: '',
  handoffRules: '',
  toneRules: '',
  offscopeRules: '',
  antiManipulationRules: '',
  extraRules: '',
  rulesProfile: defaultRulesProfile,
  companyContextExtra: '',
  scopeExtras: '',
  neverDo: '',
  desiredCategoryCount: '',
  desiredCategoryNames: '',
  desiredFunnel: '',
  suggestedCategories: [],
  voiceExamples: '',
};

const personalityOptions = [
  {
    id: 'consultor-especialista',
    title: 'Consultor Especialista',
    summary: 'Autoridade clara que diagnostica, simplifica opcoes e conduz decisoes sem parecer tecnico demais.',
    bestFor: ['inbound', 'growth'] as AgenticFunnelKind[],
  },
  {
    id: 'atendente-amigavel',
    title: 'Atendente Amigavel',
    summary: 'Cria conexao no inicio, reduz inseguranca e deixa a conversa leve sem perder foco comercial.',
    bestFor: ['inbound'] as AgenticFunnelKind[],
  },
  {
    id: 'resolutivo',
    title: 'Resolutivo',
    summary: 'Rapido, objetivo e operacional. Bom para volume, recompra, B2B e clientes que ja sabem o que querem.',
    bestFor: ['outbound', 'growth'] as AgenticFunnelKind[],
  },
  {
    id: 'vendedor-direto',
    title: 'Vendedor Direto',
    summary: 'Fechador: avanca para proposta, escolha e pagamento quando o lead ja demonstrou intencao.',
    bestFor: ['outbound'] as AgenticFunnelKind[],
  },
  {
    id: 'criativo-inspirador',
    title: 'Criativo Inspirador',
    summary: 'Ajuda o cliente a visualizar possibilidades, comparar caminhos e ganhar vontade de seguir.',
    bestFor: ['inbound', 'growth'] as AgenticFunnelKind[],
  },
  {
    id: 'urgencia-escassez',
    title: 'Urgencia e Escassez',
    summary: 'Usa prazos, agenda e capacidade real para acelerar decisoes no momento certo, sem pressao falsa.',
    bestFor: ['outbound', 'growth'] as AgenticFunnelKind[],
  },
];

function SparklesIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l1.45 5.2L18 10l-4.55 2.8L12 18l-1.45-5.2L6 10l4.55-2.8L12 2z" fill="currentColor" />
      <path d="M19 14l.72 2.35L22 17.5l-2.28 1.15L19 21l-.72-2.35L16 17.5l2.28-1.15L19 14zM5 13l.9 2.8L9 17l-3.1 1.2L5 21l-.9-2.8L1 17l3.1-1.2L5 13z" fill="currentColor" opacity=".72" />
    </svg>
  );
}

function CategoryCard({
  category,
  index,
  onEdit,
  onDelete,
}: {
  category: Category;
  index?: number;
  onEdit?: (category: Category) => void;
  onDelete?: (category: Category) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm cursor-grab active:cursor-grabbing touch-none"
    >
      <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5" style={{ backgroundColor: category.cor || '#0f172a' }} />
      <div className="flex items-start gap-4 p-4 pl-5">
        {typeof index === 'number' ? (
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
            {index + 1}
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: category.cor }} />
            <h4 className="truncate text-sm font-semibold text-slate-900">{category.nome}</h4>
          </div>
          <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500">
            {category.descricao || 'Sem descrição'}
          </p>
        </div>

        {onEdit || onDelete ? (
          <div className="flex items-center gap-2" onPointerDown={(event) => event.stopPropagation()}>
            {onEdit ? (
              <button
                onClick={() => onEdit(category)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700"
                aria-label={`Editar ${category.nome}`}
                title="Editar categoria"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
              </button>
            ) : null}
            {onDelete ? (
              <button
                onClick={() => onDelete(category)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-500 text-white transition hover:bg-red-600"
                aria-label={`Remover ${category.nome}`}
              >
                ×
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FunnelFormModal({
  isOpen,
  initial,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  initial: FunnelFormState;
  onClose: () => void;
  onSave: (data: FunnelFormState) => void;
}) {
  const [form, setForm] = useState<FunnelFormState>(initial);

  useEffect(() => {
    setForm(initial);
  }, [initial, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{initial.nome ? 'Editar funil' : 'Novo funil'}</h2>
            <p className="text-sm text-gray-500">Configure o funil e defina se ele é inbound.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Fechar
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Nome</label>
            <input
              value={form.nome}
              onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
              className="input-field"
              placeholder="Ex: Vendas B2B"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Cor</label>
            <input
              type="color"
              value={form.cor}
              onChange={(e) => setForm((prev) => ({ ...prev, cor: e.target.value }))}
              className="w-16 h-12 border border-gray-300 rounded-xl cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Descrição</label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
              className="input-field resize-none"
              rows={3}
              placeholder="Descreva o objetivo do funil"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50">
            <div>
              <p className="text-sm font-semibold text-gray-700">Funil inbound</p>
              <p className="text-xs text-gray-500">Apenas um funil pode ser inbound.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, isInbound: !prev.isInbound }))}
              className={`w-11 h-6 rounded-full transition-colors duration-200 relative focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                form.isInbound ? 'bg-green-600 focus:ring-green-500' : 'bg-gray-300 focus:ring-gray-400'
              }`}
              aria-label={form.isInbound ? 'Desativar inbound' : 'Ativar inbound'}
            >
              <span
                className={`absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white border border-gray-200 transition-transform duration-200 ${
                  form.isInbound ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(form)}
            className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl font-medium hover:bg-blue-700"
          >
            Salvar funil
          </button>
        </div>
      </div>
    </div>
  );
}

function AgenticFunnelWizard({
  isOpen,
  isSaving,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onCreate: (data: AgenticWizardState, draftFunnelId?: string) => Promise<void>;
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<AgenticWizardState>(defaultAgenticWizardState);
  const [draftFunnelId, setDraftFunnelId] = useState<string>(() => crypto.randomUUID());
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [isSuggestingCategories, setIsSuggestingCategories] = useState(false);
  const [categoriesNeedReview, setCategoriesNeedReview] = useState(true);
  const [aiTransitionMessage, setAiTransitionMessage] = useState('');
  const steps = ['Identidade', 'Personalidade', 'Regras', 'Contexto', 'Escopo', 'Nunca fazer', 'Categorias', 'Funil'];
  const progress = Math.round(((step + 1) / steps.length) * 100);

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setForm(defaultAgenticWizardState);
      setDraftFunnelId(crypto.randomUUID());
      setCategoriesNeedReview(true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const update = <K extends keyof AgenticWizardState>(field: K, value: AgenticWizardState[K]) => {
    if (field !== 'cor' && field !== 'suggestedCategories') setCategoriesNeedReview(true);
    setForm((prev) => ({ ...prev, [field]: value,
      ...(['extraRules', 'personalidade', 'voiceExamples'].includes(field) ? { regrasComunicacao: '' } : {}),
    }));
  };

  const recommended = [...personalityOptions].sort((a, b) => Number(b.bestFor.includes(form.kind)) - Number(a.bestFor.includes(form.kind)));
  const canAdvance = Boolean(form.nome.trim() && form.aiAgentName.trim() && (step === 0 || form.personalidade.trim()));
  const creationIssues = getAgenticDraftIssues(form);

  const persistCurrentStep = async (source: AgenticWizardState = form) => {
    // Keep construction local. No active inbound funnel changes until completion.
    setForm(source);
    return draftFunnelId;
  };

  const generateSuggestedCategories = async (source: AgenticWizardState = form) => {
    setIsSuggestingCategories(true);
    try {
      const suggestion = await apiService.suggestAgenticFunnelCategories({ ...source, suggestedCategories: undefined });
      setForm((prev) => ({ ...prev, suggestedCategories: suggestion.categories || [] }));
      setCategoriesNeedReview(false);
      if (suggestion.fallback) {
        toast.error('Nao consegui gerar com IA agora; carreguei uma sugestao base para voce revisar.');
      } else {
        toast.success(suggestion.knowledgeContextFound ? 'Sugestao criada com IA e base de conhecimento' : 'Sugestao criada com IA');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao gerar categorias';
      toast.error(message);
      throw error;
    } finally {
      setIsSuggestingCategories(false);
    }
  };

  const prepareStepWithAi = async (source: AgenticWizardState) => {
    const stepMap: Record<number, { step: string; field: keyof FunnelAgentBaseState; label: string }> = {
      2: { step: 'rules', field: 'regrasComunicacao', label: 'Regras melhoradas pela IA' },
      3: { step: 'context', field: 'contextoEmpresa', label: 'Contexto resumido pela IA' },
      4: { step: 'scope', field: 'escopoLimites', label: 'Escopo sugerido pela IA' },
      5: { step: 'restrictions', field: 'restricoes', label: 'Restricoes melhoradas pela IA' },
    };
    const config = stepMap[step];
    if (!config) return source;
    const correctionField = step === 3 ? 'companyContextExtra' : step === 4 ? 'scopeExtras' : step === 5 ? 'neverDo' : null;
    if (String(source[config.field] || '').trim() && (!correctionField || !source[correctionField].trim())) return source;
    const prepared = await apiService.prepareAgenticFunnelStep(config.step, { ...source, suggestedCategories: undefined });
    const improved = String(prepared[config.field] || '').trim();
    if (!improved) return source;
    toast.success(prepared.knowledgeContextFound ? `${config.label} com base de conhecimento` : config.label);
    return { ...source, [config.field]: improved, ...(correctionField ? { [correctionField]: '' } : {}) } as AgenticWizardState;
  };

  const prepareUpcomingStepWithAi = async (nextStep: number, source: AgenticWizardState) => {
    const stepMap: Record<number, { step: string; field: keyof FunnelAgentBaseState; label: string }> = {
      2: { step: 'rules', field: 'regrasComunicacao', label: 'Regras preparadas para revisao' },
      3: { step: 'context', field: 'contextoEmpresa', label: 'Contexto da proxima etapa preparado' },
      4: { step: 'scope', field: 'escopoLimites', label: 'Escopo da proxima etapa preparado' },
      5: { step: 'restrictions', field: 'restricoes', label: 'Restricoes da proxima etapa preparadas' },
    };
    const config = stepMap[nextStep];
    if (!config) return source;
    if (String(source[config.field] || '').trim()) return source;

    const prepared = await apiService.prepareAgenticFunnelStep(config.step, { ...source, suggestedCategories: undefined });
    const improved = String(prepared[config.field] || '').trim();
    if (!improved) return source;
    toast.success(prepared.knowledgeContextFound ? `${config.label} com base de conhecimento` : config.label);
    return { ...source, [config.field]: improved } as AgenticWizardState;
  };

  const prepareContextPreview = async () => {
    setIsSavingStep(true);
    try {
      const prepared = await apiService.prepareAgenticFunnelStep('context', { ...form, suggestedCategories: undefined });
      const improved = String(prepared.contextoEmpresa || '').trim();
      if (improved) {
        setForm((prev) => ({ ...prev, contextoEmpresa: improved, companyContextExtra: '' }));
        setCategoriesNeedReview(true);
        toast.success(prepared.knowledgeContextFound ? 'Contexto encontrado na base de conhecimento' : 'Contexto preparado pela IA');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao buscar contexto da empresa';
      toast.error(message);
    } finally {
      setIsSavingStep(false);
    }
  };

  const preparePersonalityPreview = async () => {
    setIsSavingStep(true);
    try {
      const prepared = await apiService.prepareAgenticFunnelStep('personality', { ...form, suggestedCategories: undefined });
      if (prepared.personalidade) update('personalidade', String(prepared.personalidade));
      toast.success('Voz preparada para revisao');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao preparar a voz');
    } finally {
      setIsSavingStep(false);
    }
  };

  const prepareScopePreview = async () => {
    setIsSavingStep(true);
    try {
      const prepared = await apiService.prepareAgenticFunnelStep('scope', { ...form, suggestedCategories: undefined });
      const improved = String(prepared.escopoLimites || '').trim();
      if (improved) {
        setForm((prev) => ({ ...prev, escopoLimites: improved, scopeExtras: '' }));
        setCategoriesNeedReview(true);
        toast.success(prepared.knowledgeContextFound ? 'Escopo sugerido com base de conhecimento' : 'Escopo sugerido pela IA');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao sugerir escopo e limites';
      toast.error(message);
    } finally {
      setIsSavingStep(false);
    }
  };

  const prepareRestrictionsPreview = async () => {
    setIsSavingStep(true);
    try {
      const prepared = await apiService.prepareAgenticFunnelStep('restrictions', { ...form, suggestedCategories: undefined });
      const improved = String(prepared.restricoes || '').trim();
      if (improved) {
        setForm((prev) => ({ ...prev, restricoes: improved, neverDo: '' }));
        setCategoriesNeedReview(true);
        toast.success(prepared.knowledgeContextFound ? 'Restricoes sugeridas com base de conhecimento' : 'Restricoes sugeridas pela IA');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao sugerir restricoes';
      toast.error(message);
    } finally {
      setIsSavingStep(false);
    }
  };

  const goNext = async () => {
    if (!canAdvance) return;
    setIsSavingStep(true);
    const transitionMessages: Record<number, string> = {
      0: 'Preparando seu rascunho...',
      1: 'IA analisando personalidade e preparando regras do agente...',
      2: 'IA analisando regras e gerando contexto permanente...',
      3: 'IA consultando a base e gerando escopos e limites...',
      4: 'IA analisando limites e gerando restricoes seguras...',
      5: 'IA preparando as perguntas para montar as categorias...',
      6: 'IA criando as categorias do funil com base nas suas respostas...',
    };
    setAiTransitionMessage(transitionMessages[step] || 'IA analisando e preparando a proxima etapa...');
    try {
      let preparedForm = form;
      preparedForm = await prepareStepWithAi(preparedForm);
      if (preparedForm !== form) {
        setForm(preparedForm);
        toast.success('Texto atualizado. Revise esta etapa e clique em Continuar.');
        return;
      }
      await persistCurrentStep(preparedForm);
      const nextStep = step + 1;
      preparedForm = await prepareUpcomingStepWithAi(nextStep, preparedForm);
      if (preparedForm !== form) setForm(preparedForm);
      if (step === 6 && (categoriesNeedReview || preparedForm.suggestedCategories.length === 0)) {
        await generateSuggestedCategories(preparedForm);
      }
      setStep((prev) => prev + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar etapa';
      toast.error(message);
    } finally {
      setIsSavingStep(false);
      setIsSuggestingCategories(false);
      setAiTransitionMessage('');
    }
  };

  const updateSuggestedCategory = (index: number, field: keyof AgenticFunnelCategorySuggestion, value: string) => {
    setForm((prev) => ({
      ...prev,
      suggestedCategories: prev.suggestedCategories.map((category, itemIndex) => itemIndex === index ? { ...category, [field]: value } : category),
    }));
  };

  const addSuggestedCategory = () => {
    setForm((prev) => {
      if (prev.suggestedCategories.length >= 7) return prev;
      return {
        ...prev,
        desiredCategoryCount: String(prev.suggestedCategories.length + 1),
        suggestedCategories: [
          ...prev.suggestedCategories,
          {
            nome: `Nova etapa ${prev.suggestedCategories.length + 1}`,
            descricao: 'Entrar quando: \nPermanecer enquanto: \nSair quando: \nNao usar quando: ',
            agentMainObjective: '',
            exemplosMensagens: '',
          },
        ],
      };
    });
  };

  const removeSuggestedCategory = (index: number) => {
    setForm((prev) => {
      if (prev.suggestedCategories.length <= 2) return prev;
      return {
        ...prev,
        desiredCategoryCount: String(prev.suggestedCategories.length - 1),
        suggestedCategories: prev.suggestedCategories.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  };

  const updateRulesProfile = <K extends keyof AgenticRulesProfile>(field: K, value: AgenticRulesProfile[K]) => {
    setCategoriesNeedReview(true);
    setForm((prev) => ({ ...prev, rulesProfile: { ...prev.rulesProfile, [field]: value }, regrasComunicacao: '' }));
  };

  const toggleHandoffTrigger = (trigger: string) => {
    setCategoriesNeedReview(true);
    setForm((prev) => {
      const exists = prev.rulesProfile.handoffTriggers.includes(trigger);
      return {
        ...prev,
        regrasComunicacao: '',
        rulesProfile: {
          ...prev.rulesProfile,
          handoffTriggers: exists
            ? prev.rulesProfile.handoffTriggers.filter((item) => item !== trigger)
            : [...prev.rulesProfile.handoffTriggers, trigger],
        },
      };
    });
  };

  const ChoiceButton = ({ active, children, onClick }: { active: boolean; children: string; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-3 py-2 text-left text-sm font-semibold transition ${active ? 'border-violet-400 bg-violet-50 text-violet-900 ring-2 ring-violet-100' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-violet-100 bg-white shadow-2xl">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-violet-950 to-fuchsia-900 px-6 py-5 text-white">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-violet-100">
                <SparklesIcon /> Novo Funil Agentico
              </div>
              <h2 className="mt-3 text-2xl font-bold">Construtor guiado de funil</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-violet-100">Cada etapa transforma respostas em base de prompt, regras e categorias prontas para um agente comercial.</p>
            </div>
            <button disabled={isSavingStep || isSuggestingCategories || isSaving} onClick={onClose} className="rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/20">Fechar</button>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold text-violet-100"><span>{steps[step]}</span><span>{progress}%</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-gradient-to-r from-cyan-300 to-fuchsia-300" style={{ width: `${progress}%` }} /></div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[220px_1fr]">
          <aside className="border-b border-slate-200 bg-slate-50 p-4 lg:border-b-0 lg:border-r">
            <div className="space-y-2">
              {steps.map((item, index) => (
                <button key={item} type="button" disabled={index > step || isSavingStep || isSuggestingCategories || isSaving} onClick={() => setStep(index)} className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left text-sm transition ${index === step ? 'bg-violet-600 text-white shadow-sm' : index < step ? 'bg-white text-slate-900' : 'text-slate-500 hover:bg-white'}`}>
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${index === step ? 'bg-white/20' : 'bg-slate-200'}`}>{index + 1}</span>
                  <span className="font-semibold">{item}</span>
                </button>
              ))}
            </div>
          </aside>

          <main className="p-5 md:p-7">
            {aiTransitionMessage ? (
              <div className="flex min-h-[430px] items-center justify-center text-center">
                <div className="max-w-md rounded-[28px] border border-violet-200 bg-violet-50 px-8 py-10 shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-violet-700 shadow-sm">
                    <div className="h-7 w-7 animate-spin rounded-full border-2 border-violet-200 border-t-violet-700" />
                  </div>
                  <p className="mt-5 text-base font-semibold text-violet-950">{aiTransitionMessage}</p>
                  <p className="mt-2 text-sm leading-6 text-violet-700">Preparando a proxima tela para revisao. O atendimento atual so muda quando voce concluir o funil.</p>
                </div>
              </div>
            ) : (<>
            {step === 0 && (
              <div className="grid gap-4 md:grid-cols-2">
                <div><label className="mb-1 block text-sm font-semibold text-slate-700">Nome do funil</label><input className="input-field rounded-2xl" value={form.nome} onChange={(e) => update('nome', e.target.value)} placeholder="Ex: Orcamento personalizado" /></div>
                <div><label className="mb-1 block text-sm font-semibold text-slate-700">Nome do agente</label><input className="input-field rounded-2xl" value={form.aiAgentName} onChange={(e) => update('aiAgentName', e.target.value)} placeholder="Ex: Ana" /></div>
                <div className="md:col-span-2"><label className="mb-1 block text-sm font-semibold text-slate-700">O que sua empresa oferece e quem o agente atende?</label><textarea rows={3} className="input-field rounded-2xl" value={form.descricao} onChange={(e) => update('descricao', e.target.value)} placeholder="Ex: vendemos pecas para artesaos. O agente tira duvidas e orienta compras. A base de conhecimento complementa essas informacoes." /></div>
                <div className="md:col-span-2"><label className="mb-2 block text-sm font-semibold text-slate-700">Tipo de funil</label><div className="grid gap-3 md:grid-cols-3">{(['inbound', 'outbound', 'growth'] as AgenticFunnelKind[]).map((kind) => (<button key={kind} type="button" onClick={() => update('kind', kind)} className={`rounded-2xl border p-4 text-left capitalize ${form.kind === kind ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-100' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>{kind}</button>))}</div></div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Escolha a personalidade base</h3>
                <p className="mt-1 text-sm text-slate-500">Ordenei do mais recomendado ao menos recomendado para o tipo selecionado.</p>
                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  {recommended.map((option, index) => (<button key={option.id} type="button" onClick={() => update('personalidade', `${option.title}: ${option.summary}`)} className={`rounded-2xl border p-4 text-left transition ${form.personalidade.startsWith(option.title) ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-100' : 'border-slate-200 hover:bg-slate-50'}`}><div className="flex items-center justify-between gap-3"><span className="font-semibold text-slate-900">{option.title}</span>{index < 2 && <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700">Recomendado</span>}</div><p className="mt-2 text-sm leading-5 text-slate-600">{option.summary}</p></button>))}
                </div>
                <div className="mt-5 space-y-3">
                  <label className="block text-sm font-semibold text-slate-700">Jeito de falar do agente</label>
                  <textarea rows={5} className="input-field rounded-xl" value={form.personalidade} disabled={isSavingStep} onChange={(e) => update('personalidade', e.target.value)} placeholder="Escolha uma base acima ou descreva a voz da sua marca." />
                  <label className="block text-sm font-semibold text-slate-700">Exemplos de como sua equipe escreve (opcional)</label>
                  <textarea rows={3} className="input-field rounded-xl" value={form.voiceExamples} disabled={isSavingStep} onChange={(e) => update('voiceExamples', e.target.value)} placeholder="Cole duas ou tres mensagens, sem dados pessoais. A IA usa o jeito de escrever, sem copiar os fatos." />
                  <button type="button" disabled={isSavingStep} onClick={preparePersonalityPreview} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{isSavingStep ? 'Preparando...' : 'Adaptar voz com IA'}</button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Regras do agente</h3>
                  <p className="mt-1 text-sm text-slate-500">A IA preparou as regras com os dados da empresa. Revise o texto abaixo ou abra as opcoes para personalizar.</p>
                </div>

                <details className="space-y-4">
                  <summary className="cursor-pointer rounded-xl bg-slate-100 p-3 text-sm font-semibold text-slate-800">Personalizar regras comerciais, tom e encaminhamento</summary>
                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Preco, desconto e parcelamento</h4>
                  <p className="mt-3 text-sm font-semibold text-slate-700">Se o cliente pedir desconto, o que o agente pode fazer?</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-4">
                    <ChoiceButton active={form.rulesProfile.discountMode === 'none'} onClick={() => updateRulesProfile('discountMode', 'none')}>Nao oferecer desconto</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.discountMode === 'no_negotiation'} onClick={() => updateRulesProfile('discountMode', 'no_negotiation')}>Agente nao negocia</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.discountMode === 'limited'} onClick={() => updateRulesProfile('discountMode', 'limited')}>Pode dar desconto ate um limite</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.discountMode === 'human_approval'} onClick={() => updateRulesProfile('discountMode', 'human_approval')}>Pode negociar, mas acima do limite chama humano</ChoiceButton>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <input className="input-field rounded-xl bg-white" value={form.rulesProfile.maxDiscountPercent} onChange={(e) => updateRulesProfile('maxDiscountPercent', e.target.value)} placeholder="Desconto maximo %" />
                    <input className="input-field rounded-xl bg-white" value={form.rulesProfile.approvalAbovePercent} onChange={(e) => updateRulesProfile('approvalAbovePercent', e.target.value)} placeholder="Aprovar acima de %" />
                    <input className="input-field rounded-xl bg-white" value={form.rulesProfile.maxInstallments} onChange={(e) => updateRulesProfile('maxInstallments', e.target.value)} placeholder="Parcelas maximas" />
                  </div>
                  <p className="mt-4 text-sm font-semibold text-slate-700">Se o cliente disser que esta caro, qual deve ser a resposta padrao?</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <ChoiceButton active={form.rulesProfile.expensiveObjection === 'justify_value'} onClick={() => updateRulesProfile('expensiveObjection', 'justify_value')}>Explicar o valor antes de negociar</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.expensiveObjection === 'offer_alternative'} onClick={() => updateRulesProfile('expensiveObjection', 'offer_alternative')}>Oferecer alternativa mais barata</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.expensiveObjection === 'call_human'} onClick={() => updateRulesProfile('expensiveObjection', 'call_human')}>Chamar humano para negociar</ChoiceButton>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Promessas e garantias</h4>
                  <p className="mt-3 text-sm font-semibold text-slate-700">Quando o cliente perguntar prazo de entrega, o agente deve responder como?</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <ChoiceButton active={form.rulesProfile.deliveryPromise === 'system_only'} onClick={() => updateRulesProfile('deliveryPromise', 'system_only')}>Informar so se estiver no sistema ou base</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.deliveryPromise === 'estimate_language'} onClick={() => updateRulesProfile('deliveryPromise', 'estimate_language')}>Pode estimar, mas usando linguagem de previsao</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.deliveryPromise === 'human_only'} onClick={() => updateRulesProfile('deliveryPromise', 'human_only')}>Nao promete prazo, chama humano</ChoiceButton>
                  </div>
                  <p className="mt-4 text-sm font-semibold text-slate-700">Quando o cliente pedir garantia de resultado, o que o agente pode prometer?</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <ChoiceButton active={form.rulesProfile.resultGuarantee === 'never'} onClick={() => updateRulesProfile('resultGuarantee', 'never')}>Nunca garantir resultado perfeito</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.resultGuarantee === 'documented_only'} onClick={() => updateRulesProfile('resultGuarantee', 'documented_only')}>Garantir somente o que estiver documentado</ChoiceButton>
                    <ChoiceButton active={form.rulesProfile.resultGuarantee === 'human_only'} onClick={() => updateRulesProfile('resultGuarantee', 'human_only')}>Garantia fora do padrao chama humano</ChoiceButton>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Quando chamar humano</h4>
                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                    {[
                      ['human_requested', 'Cliente pediu pessoa'],
                      ['serious_complaint', 'Reclamacao seria'],
                      ['refund', 'Reembolso'],
                      ['angry_customer', 'Cliente irritado'],
                      ['legal', 'Pergunta juridica'],
                      ['high_value', 'Valor alto'],
                    ].map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                        <input type="checkbox" checked={form.rulesProfile.handoffTriggers.includes(value)} onChange={() => toggleHandoffTrigger(value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  <input className="mt-3 input-field rounded-xl bg-white" value={form.rulesProfile.handoffValueAbove} onChange={(e) => updateRulesProfile('handoffValueAbove', e.target.value)} placeholder="Valor alto acima de R$" />
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Tom, escopo e seguranca</h4>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <label className="text-xs font-semibold text-slate-600">Qual tom o agente deve usar?<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.toneStyle} onChange={(e) => updateRulesProfile('toneStyle', e.target.value as AgenticRulesProfile['toneStyle'])}><option value="professional">Profissional</option><option value="friendly">Amigavel</option><option value="informal">Informal</option></select></label>
                    <label className="text-xs font-semibold text-slate-600">Pode usar emoji nas mensagens?<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.emojiLevel} onChange={(e) => updateRulesProfile('emojiLevel', e.target.value as AgenticRulesProfile['emojiLevel'])}><option value="none">Nao usar emoji</option><option value="low">Usar pouco emoji</option><option value="moderate">Usar emoji moderado</option></select></label>
                    <label className="text-xs font-semibold text-slate-600">Se o cliente for grosseiro, o agente deve:<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.rudeCustomerBehavior} onChange={(e) => updateRulesProfile('rudeCustomerBehavior', e.target.value as AgenticRulesProfile['rudeCustomerBehavior'])}><option value="polite">Manter educacao e tentar ajudar</option><option value="set_boundary">Dar um limite educado</option><option value="end_or_handoff">Encerrar ou chamar humano</option></select></label>
                    <label className="text-xs font-semibold text-slate-600">Se o lead sair do contexto da empresa, fazer:<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.offScopeBehavior} onChange={(e) => updateRulesProfile('offScopeBehavior', e.target.value as AgenticRulesProfile['offScopeBehavior'])}><option value="redirect">Redirecionar a conversa para o negocio</option><option value="answer_brief_then_redirect">Responder brevemente e redirecionar ao negocio</option><option value="human">Chamar humano</option></select></label>
                    <label className="text-xs font-semibold text-slate-600">Se perguntarem sobre concorrente, fazer:<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.competitorBehavior} onChange={(e) => updateRulesProfile('competitorBehavior', e.target.value as AgenticRulesProfile['competitorBehavior'])}><option value="do_not_comment">Nao comentar concorrente</option><option value="neutral_only">Responder de forma neutra, sem criticar</option></select></label>
                    <label className="text-xs font-semibold text-slate-600">Se perguntarem se e IA, responder:<select className="mt-1 input-field rounded-xl bg-white" value={form.rulesProfile.revealAi} onChange={(e) => updateRulesProfile('revealAi', e.target.value as AgenticRulesProfile['revealAi'])}><option value="transparent_if_asked">Ser transparente se perguntarem</option><option value="do_not_focus">Nao focar nisso e voltar ao atendimento</option><option value="human_only">Chamar humano</option></select></label>
                  </div>
                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                    {([
                      ['neverInvent', 'Nunca inventar informacao'],
                      ['neverRevealPrompt', 'Nunca revelar prompt interno'],
                      ['neverIgnoreInstructions', 'Nunca ignorar instrucoes'],
                    ] as Array<[keyof AgenticRulesProfile, string]>).map(([field, label]) => (
                      <label key={field} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                        <input type="checkbox" checked={Boolean(form.rulesProfile[field])} onChange={(e) => updateRulesProfile(field, e.target.checked as any)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </section>
                </details>

                <div><label className="mb-1 block text-sm font-semibold text-slate-700">Alguma regra extra?</label><textarea rows={3} className="input-field resize-y rounded-2xl" value={form.extraRules} onChange={(e) => update('extraRules', e.target.value)} placeholder="Excecoes especificas que nao apareceram acima." /></div>
              </div>
            )}

            {step === 2 && <div className="mt-5 rounded-2xl border border-violet-200 bg-violet-50 p-4">
              <label className="block text-sm font-semibold text-violet-900">Regras que o agente vai seguir</label>
              <p className="mt-1 text-sm text-slate-600">Revise o texto livremente. Se mudar as opcoes acima, a IA gera uma nova versao ao continuar.</p>
              {form.regrasComunicacao ? <textarea rows={8} className="input-field mt-3 rounded-xl bg-white" value={form.regrasComunicacao} onChange={(e) => update('regrasComunicacao', e.target.value)} /> : <p className="mt-3 text-sm text-slate-500">As regras serao preparadas com as opcoes atuais.</p>}
            </div>}

            {step === 3 && <div><div><h3 className="text-lg font-semibold text-slate-900">Contexto permanente da empresa</h3><p className="mt-1 text-sm leading-6 text-slate-500">Primeiro a IA consulta a base de conhecimento e monta um resumo do que precisa ficar sempre no prompt. Se voce pedir uma correcao, ela reescreve o resumo e mostra aqui de novo antes de avancar.</p></div>{form.contextoEmpresa ? <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"><div className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">Resumo atual da IA</div><textarea rows={8} className="input-field resize-y rounded-xl bg-white" value={form.contextoEmpresa} onChange={(e) => update('contextoEmpresa', e.target.value)} /></div> : <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">Clique em consultar base para a IA buscar contexto da empresa antes de voce complementar.</div>}<div className="mt-5"><label className="mb-1 block text-sm font-semibold text-slate-700">Quer adicionar, remover ou corrigir algo nesse resumo?</label><textarea rows={5} className="input-field resize-y rounded-2xl" value={form.companyContextExtra} onChange={(e) => update('companyContextExtra', e.target.value)} placeholder="Ex: remova X, adicione Y, corrija Z." /><button type="button" disabled={isSavingStep} onClick={prepareContextPreview} className="mt-3 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300">{isSavingStep ? 'Consultando...' : form.contextoEmpresa ? 'Aplicar correcao' : 'Consultar base'}</button></div></div>}

            {step === 4 && <div><div><h3 className="text-lg font-semibold text-slate-900">Escopos e limites</h3><p className="mt-1 text-sm leading-6 text-slate-500">Primeiro a IA analisa o funil e a base de conhecimento. Se voce pedir uma mudanca, ela reescreve o escopo e mostra aqui novamente.</p></div>{form.escopoLimites ? <div className="mt-5 rounded-2xl border border-cyan-200 bg-cyan-50 p-4"><div className="mb-2 text-xs font-bold uppercase tracking-wide text-cyan-700">Escopo atual da IA</div><textarea rows={8} className="input-field resize-y rounded-xl bg-white" value={form.escopoLimites} onChange={(e) => update('escopoLimites', e.target.value)} /></div> : <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">Clique em sugerir escopo para a IA consultar o contexto antes de voce complementar.</div>}<div className="mt-5"><label className="mb-1 block text-sm font-semibold text-slate-700">Quer adicionar, remover ou corrigir algum limite?</label><textarea rows={5} className="input-field resize-y rounded-2xl" value={form.scopeExtras} onChange={(e) => update('scopeExtras', e.target.value)} placeholder="Ex: remova X, adicione Y, todo caso Z chama humano." /><button type="button" disabled={isSavingStep} onClick={prepareScopePreview} className="mt-3 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300">{isSavingStep ? 'Consultando...' : form.escopoLimites ? 'Aplicar correcao' : 'Sugerir escopo'}</button></div></div>}

            {step === 5 && <div><div><h3 className="text-lg font-semibold text-slate-900">O que nunca fazer</h3><p className="mt-1 text-sm leading-6 text-slate-500">Primeiro a IA sugere restricoes seguras. Se voce pedir uma mudanca, ela reescreve a lista e mostra aqui novamente antes de avancar.</p></div>{form.restricoes ? <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4"><div className="mb-2 text-xs font-bold uppercase tracking-wide text-rose-700">Restricoes atuais da IA</div><textarea rows={8} className="input-field resize-y rounded-xl bg-white" value={form.restricoes} onChange={(e) => update('restricoes', e.target.value)} /></div> : <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">Clique em sugerir restricoes para a IA montar uma lista inicial antes de voce complementar.</div>}<div className="mt-5"><label className="mb-1 block text-sm font-semibold text-slate-700">Quer adicionar, remover ou corrigir alguma restricao?</label><textarea rows={5} className="input-field resize-y rounded-2xl" value={form.neverDo} onChange={(e) => update('neverDo', e.target.value)} placeholder="Ex: remova X, adicione Y, nunca fazer Z." /><button type="button" disabled={isSavingStep} onClick={prepareRestrictionsPreview} className="mt-3 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300">{isSavingStep ? 'Consultando...' : form.restricoes ? 'Aplicar correcao' : 'Sugerir restricoes'}</button></div></div>}

            {step === 6 && <div><h3 className="text-lg font-semibold text-slate-900">Como quer montar as categorias?</h3><p className="mt-1 text-sm leading-6 text-slate-500">Responda antes da IA criar o funil. Na proxima tela voce revisa as categorias geradas.</p><div className="mt-5 grid gap-4 md:grid-cols-[220px_1fr]"><label className="text-sm font-semibold text-slate-700">Quantas categorias?<select className="mt-1 input-field rounded-2xl bg-white" value={form.desiredCategoryCount} onChange={(e) => update('desiredCategoryCount', e.target.value)}><option value="">Deixar IA decidir</option>{[2, 3, 4, 5, 6, 7].map((count) => <option key={count} value={String(count)}>{count} categorias</option>)}</select></label><label className="text-sm font-semibold text-slate-700">Quais categorias voce quer?<textarea rows={4} className="mt-1 input-field resize-y rounded-2xl" value={form.desiredCategoryNames} onChange={(e) => update('desiredCategoryNames', e.target.value)} placeholder="Ex: Entrada, Qualificacao, Diagnostico, Proposta, Fechamento, Pos-venda." /></label></div><label className="mt-4 block text-sm font-semibold text-slate-700">Regras para montar o funil<textarea rows={4} className="mt-1 input-field resize-y rounded-2xl" value={form.desiredFunnel} onChange={(e) => update('desiredFunnel', e.target.value)} placeholder="Opcional: ordem desejada, etapas obrigatorias, etapas que nao quer, ou como a IA deve organizar o funil." /></label></div>}

            {step === 7 && <div className="mb-5 rounded-2xl border border-violet-200 bg-violet-50 p-4 text-sm text-violet-950">
              <p className="font-semibold">Revisao antes de criar</p>
              <p className="mt-1">A voz e as regras sao compartilhadas. Cada etapa define quando atuar, o que resolver e quando encerrar ou seguir.</p>
              {form.kind === 'inbound' && <p className="mt-2">Ao concluir, este sera o funil de entrada para novos contatos. O funil atual permanece ativo ate todas as etapas serem salvas.</p>}
              {categoriesNeedReview && <p className="mt-2 font-semibold">A base ou o pedido mudou. Use Gerar novamente para atualizar as etapas; a versao anterior foi mantida para consulta.</p>}
              {creationIssues.length > 0 && <ul className="mt-3 list-disc pl-5">{creationIssues.map((issue, index) => <li key={index}>{issue}</li>)}</ul>}
            </div>}
            {step === 7 && <div><h3 className="text-lg font-semibold text-slate-900">Revise as categorias sugeridas pela IA</h3><p className="mt-1 text-sm leading-6 text-slate-500">Ajuste as categorias geradas. Para pedir uma nova versao, escreva uma observacao para a IA.</p>{isSuggestingCategories && <div className="mt-4 rounded-2xl border border-violet-100 bg-violet-50 p-4 text-sm font-semibold text-violet-900">Gerando categorias com IA...</div>}<div className="mt-4 flex flex-col gap-3 md:flex-row md:items-start"><textarea rows={3} className="input-field resize-y rounded-2xl" value={form.desiredFunnel} onChange={(e) => update('desiredFunnel', e.target.value)} placeholder="Ex: nao era bem isso; quero um funil mais consultivo antes da proposta." /><button type="button" disabled={isSuggestingCategories || isSaving} onClick={() => { void generateSuggestedCategories().catch(() => {}); }} className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300">{isSuggestingCategories ? 'Gerando...' : 'Gerar novamente'}</button></div><div className="mt-5 flex items-center justify-between gap-3"><span className="text-sm font-semibold text-slate-700">{form.suggestedCategories.length} categorias</span><button type="button" disabled={form.suggestedCategories.length >= 7} onClick={addSuggestedCategory} className="rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-300">Adicionar categoria</button></div><div className="mt-4 space-y-4">{form.suggestedCategories.map((category, index) => <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="mb-3 flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-sm font-bold text-white">{index + 1}</span><input className="input-field rounded-xl bg-white font-semibold" value={category.nome} onChange={(e) => updateSuggestedCategory(index, 'nome', e.target.value)} /><button type="button" disabled={form.suggestedCategories.length <= 2} onClick={() => removeSuggestedCategory(index)} className="rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300">Excluir</button></div><label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Quando entrar, permanecer e sair desta etapa</label><div className="grid gap-3 md:grid-cols-2">{stageCriteriaFields.map((field) => <label key={field.key} className="block text-sm text-slate-700"><span className="mb-1 block font-medium">{field.label}</span><textarea rows={3} className="input-field resize-y rounded-xl bg-white" placeholder={field.placeholder} value={readStageCriteria(category.descricao)[field.key]} onChange={(e) => updateSuggestedCategory(index, 'descricao', writeStageCriterion(category.descricao, field.key, e.target.value))} /></label>)}</div><label className="mb-1 mt-3 block text-xs font-bold uppercase tracking-wide text-slate-500">Objetivo desta etapa</label><textarea rows={3} className="input-field resize-y rounded-xl bg-white" value={category.agentMainObjective} onChange={(e) => updateSuggestedCategory(index, 'agentMainObjective', e.target.value)} /><label className="mb-1 mt-3 block text-xs font-bold uppercase tracking-wide text-slate-500">Exemplos de mensagens</label><textarea rows={3} className="input-field resize-y rounded-xl bg-white" value={category.exemplosMensagens} onChange={(e) => updateSuggestedCategory(index, 'exemplosMensagens', e.target.value)} /></div>)}</div></div>}            </>)}
          </main>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" disabled={isSavingStep || isSuggestingCategories || isSaving} onClick={() => (step === 0 ? onClose() : setStep((prev) => prev - 1))} className="rounded-2xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200">{step === 0 ? 'Cancelar' : 'Voltar'}</button>
          {step < steps.length - 1 ? <button type="button" disabled={!canAdvance || isSavingStep || isSuggestingCategories} onClick={goNext} className="rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-300">{isSuggestingCategories ? 'Gerando categorias...' : isSavingStep ? 'Salvando...' : 'Continuar'}</button> : <button type="button" disabled={isSaving || isSavingStep || isSuggestingCategories || categoriesNeedReview || creationIssues.length > 0 || !form.nome.trim() || !form.aiAgentName.trim() || form.suggestedCategories.length < 2 || form.suggestedCategories.length > 7} onClick={() => onCreate(form, draftFunnelId)} className="inline-flex items-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-300"><SparklesIcon />{isSaving ? 'Criando...' : 'Criar funil agentico'}</button>}
        </div>
      </div>
    </div>
  );
}

export function FunnelsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string>(searchParams.get('funnelId') || '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<Funnel | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<CategoryId | null>(null);
  const [orderedCategories, setOrderedCategories] = useState<Category[]>([]);
  const initialOrderRef = useRef<CategoryId[]>([]);
  const [funnelAgentBase, setFunnelAgentBase] = useState<FunnelAgentBaseState>(emptyFunnelAgentBase);
  const [isSavingAgentBase, setIsSavingAgentBase] = useState(false);
  const [isSavingMarketplaceMode, setIsSavingMarketplaceMode] = useState(false);
  const [isAgenticWizardOpen, setIsAgenticWizardOpen] = useState(false);
  const [isMarketplaceWizardOpen, setIsMarketplaceWizardOpen] = useState(false);
  const [isCreatingAgenticFunnel, setIsCreatingAgenticFunnel] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const selectedFunnel = useMemo(
    () => funnels.find((funnel) => funnel.id === selectedFunnelId) || null,
    [funnels, selectedFunnelId]
  );

  useEffect(() => {
    if (!selectedFunnel) {
      setFunnelAgentBase(emptyFunnelAgentBase);
      return;
    }
    setFunnelAgentBase({
      aiAgentName: selectedFunnel.aiAgentName || '',
      personalidade: selectedFunnel.personalidade || '',
      regrasComunicacao: selectedFunnel.regrasComunicacao || '',
      contextoEmpresa: selectedFunnel.contextoEmpresa || '',
      escopoLimites: selectedFunnel.escopoLimites || '',
      restricoes: selectedFunnel.restricoes || '',
    });
  }, [selectedFunnel]);

  const updateFunnelAgentBase = (field: keyof FunnelAgentBaseState, value: string) => {
    setFunnelAgentBase((prev) => ({ ...prev, [field]: value }));
  };

  const funnelIdFromUrl = searchParams.get('funnelId') || '';

  useEffect(() => {
    if (funnelIdFromUrl !== selectedFunnelId) {
      setSelectedFunnelId(funnelIdFromUrl);
    }
  }, [funnelIdFromUrl]);

  useEffect(() => {
    if (selectedFunnelId === funnelIdFromUrl) return;

    const next = new URLSearchParams(searchParams);
    if (selectedFunnelId) {
      next.set('funnelId', selectedFunnelId);
    } else {
      next.delete('funnelId');
    }
    setSearchParams(next, { replace: true });
  }, [selectedFunnelId, funnelIdFromUrl, searchParams, setSearchParams]);


  const loadData = async (preferredFunnelId?: string) => {
    try {
      const [funnelsData, categoriesData] = await Promise.all([
        apiService.getFunnels({ includeCategories: true }),
        apiService.getAllCategories(),
      ]);
      setFunnels(funnelsData);
      setCategories(categoriesData);
      const requestedFunnelId = preferredFunnelId || searchParams.get('funnelId') || selectedFunnelId;
      const requestedFunnelExists = requestedFunnelId
        ? funnelsData.some((funnel) => funnel.id === requestedFunnelId)
        : false;
      if (requestedFunnelExists && requestedFunnelId !== selectedFunnelId) {
        setSelectedFunnelId(requestedFunnelId);
      } else if (!requestedFunnelExists && funnelsData.length > 0) {
        const inbound = funnelsData.find((funnel) => funnel.isInbound);
        setSelectedFunnelId(inbound?.id || funnelsData[0].id);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar funis';
      toast.error(message);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedFunnelId) {
      setOrderedCategories([]);
      initialOrderRef.current = [];
      return;
    }
    const filtered = categories
      .filter((cat) => cat.funnelId === selectedFunnelId)
      .sort((a, b) => (a.funnelOrder ?? 0) - (b.funnelOrder ?? 0));
    setOrderedCategories(filtered);
    initialOrderRef.current = filtered.map((cat) => cat.id);
  }, [categories, selectedFunnelId]);

  const hasOrderChanges = useMemo(() => {
    if (orderedCategories.length !== initialOrderRef.current.length) return true;
    return orderedCategories.some((cat, index) => cat.id !== initialOrderRef.current[index]);
  }, [orderedCategories]);

  const hasAgentBaseChanges = useMemo(() => {
    if (!selectedFunnel) return false;
    return (
      funnelAgentBase.aiAgentName !== (selectedFunnel.aiAgentName || '') ||
      funnelAgentBase.personalidade !== (selectedFunnel.personalidade || '') ||
      funnelAgentBase.regrasComunicacao !== (selectedFunnel.regrasComunicacao || '') ||
      funnelAgentBase.contextoEmpresa !== (selectedFunnel.contextoEmpresa || '') ||
      funnelAgentBase.escopoLimites !== (selectedFunnel.escopoLimites || '') ||
      funnelAgentBase.restricoes !== (selectedFunnel.restricoes || '')
    );
  }, [funnelAgentBase, selectedFunnel]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveCategoryId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCategoryId(null);
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;
    const oldIndex = orderedCategories.findIndex((cat) => cat.id === activeId);
    const newIndex = orderedCategories.findIndex((cat) => cat.id === overId);
    if (oldIndex < 0 || newIndex < 0) return;
    setOrderedCategories((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleSaveOrder = async () => {
    if (!selectedFunnelId) return;
    try {
      await apiService.updateFunnelCategories(
        selectedFunnelId,
        orderedCategories.map((cat, index) => ({
          id: cat.id,
          funnelOrder: index,
        }))
      );
      toast.success('Ordem atualizada com sucesso');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar ordem';
      toast.error(message);
    }
  };

  const handleSaveAgentBase = async () => {
    if (!selectedFunnel) return;
    setIsSavingAgentBase(true);
    try {
      await apiService.updateFunnel(selectedFunnel.id, {
        nome: selectedFunnel.nome,
        cor: selectedFunnel.cor,
        descricao: selectedFunnel.descricao || undefined,
        isInbound: selectedFunnel.isInbound,
        aiAgentName: funnelAgentBase.aiAgentName || undefined,
        personalidade: funnelAgentBase.personalidade || undefined,
        regrasComunicacao: funnelAgentBase.regrasComunicacao || undefined,
        contextoEmpresa: funnelAgentBase.contextoEmpresa || undefined,
        escopoLimites: funnelAgentBase.escopoLimites || undefined,
        restricoes: funnelAgentBase.restricoes || undefined,
      });
      toast.success('Base do agente atualizada');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar base do agente';
      toast.error(message);
    } finally {
      setIsSavingAgentBase(false);
    }
  };

  const handleMarketplaceMode = async (enabled: boolean) => {
    if (!selectedFunnel || isSavingMarketplaceMode) return;
    setIsSavingMarketplaceMode(true);
    try {
      const updated = await apiService.setFunnelMarketplaceMode(selectedFunnel.id, enabled);
      setFunnels(current => current.map(funnel => funnel.id === updated.id ? { ...funnel, isMarketplace: updated.isMarketplace } : funnel));
      toast.success(enabled ? 'Funil para marketplace habilitado' : 'Personalização de marketplace desativada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível atualizar o funil');
    } finally {
      setIsSavingMarketplaceMode(false);
    }
  };



  const handleDeleteCategory = async (category: Category) => {
    if (!confirm(`Tem certeza que deseja excluir a categoria "${category.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await apiService.deleteCategory(category.id);
      toast.success('Categoria excluída com sucesso');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir categoria';
      toast.error(message);
    }
  };

  const handleSaveFunnel = async (data: FunnelFormState) => {
    try {
      const payload: FunnelInput = {
        nome: data.nome,
        cor: data.cor,
        descricao: data.descricao || undefined,
        isInbound: data.isInbound,
      };
      if (editingFunnel) {
        await apiService.updateFunnel(editingFunnel.id, payload);
        toast.success('Funil atualizado com sucesso');
      } else {
        const created = await apiService.createFunnel(payload);
        toast.success('Funil criado com sucesso');
        setSelectedFunnelId(created.id);
      }
      setIsFormOpen(false);
      setEditingFunnel(null);
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar funil';
      toast.error(message);
    }
  };

  const handleDeleteFunnel = async (funnel: Funnel) => {
    if (!confirm(`Excluir o funil ${funnel.nome}?`)) return;
    try {
      await apiService.deleteFunnel(funnel.id);
      toast.success('Funil removido');
      setSelectedFunnelId('');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir funil';
      toast.error(message);
    }
  };

  const handleCloneFunnel = async (funnel: Funnel) => {
    try {
      const cloned = await apiService.cloneFunnel(funnel.id);
      toast.success('Funil clonado com sucesso');
      setSelectedFunnelId(cloned.id);
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao clonar funil';
      toast.error(message);
    }
  };

  const buildAgenticBase = (data: AgenticWizardState) => {
    return {
      personalidade: data.personalidade.trim(),
      regrasComunicacao: data.regrasComunicacao.trim(),
      contextoEmpresa: data.contextoEmpresa.trim(),
      escopoLimites: data.escopoLimites.trim(),
      restricoes: data.restricoes.trim(),
    };
  };

  const handleCreateAgenticFunnel = async (data: AgenticWizardState, draftFunnelId?: string) => {
    setIsCreatingAgenticFunnel(true);
    try {
      const issues = getAgenticDraftIssues(data);
      if (issues.length) throw new Error(issues[0]);
      const base = buildAgenticBase(data);
      const stages = data.suggestedCategories;
      if (stages.length < 2 || stages.length > 7) {
        throw new Error('Confirme entre 2 e 7 categorias antes de criar o funil');
      }
      if (Object.values(base).some((value) => !value)) throw new Error('Revise as secoes do agente antes de criar o funil.');
      if (data.companyContextExtra || data.scopeExtras || data.neverDo) throw new Error('Aplique as correcoes pendentes antes de criar o funil.');
      const created = await apiService.createAgenticFunnel({
        requestId: draftFunnelId, nome: data.nome.trim(), cor: data.cor || '#7c3aed',
        descricao: data.descricao, isInbound: data.kind === 'inbound', aiAgentName: data.aiAgentName.trim(),
        ...base, categories: stages, desiredCategoryCount: data.desiredCategoryCount,
      });

      toast.success(`Funil agentico criado com ${stages.length} categorias`);
      setSelectedFunnelId(created.id);
      setIsAgenticWizardOpen(false);
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar funil agentico';
      toast.error(message);
    } finally {
      setIsCreatingAgenticFunnel(false);
    }
  };

  return (
    <>
          <Header
            title="Funis"
            subtitle="Crie funis e organize as categorias em etapas"
            actions={
              <div className="flex flex-col gap-2 sm:items-end">
                <button
                  onClick={() => {
                    setEditingFunnel(null);
                    setIsFormOpen(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  + Novo Funil
                </button>
                <button
                  onClick={() => setIsAgenticWizardOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-200 transition hover:from-violet-700 hover:to-fuchsia-700"
                >
                  <SparklesIcon />
                  Novo Funil Agentico
                </button>
                <button onClick={() => setIsMarketplaceWizardOpen(true)} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700">
                  + Novo Funil Marketplaces
                </button>
              </div>
            }
          />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_1fr]">
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-900 px-6 py-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-100">
                      Navegação
                    </span>
                    <h2 className="mt-3 text-lg font-semibold text-white">Funis cadastrados</h2>
                    <p className="mt-1 text-sm text-slate-300">Selecione um funil para ver e organizar as categorias.</p>
                  </div>
                  <button
                    onClick={() => loadData()}
                    className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
                  >
                    Atualizar
                  </button>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{funnels.length}</span> funis cadastrados
                </div>

                <div className="space-y-3">
                  {funnels.map((funnel) => {
                    const isSelected = selectedFunnelId === funnel.id;
                    const funnelCategoriesCount = categories.filter((category) => category.funnelId === funnel.id).length;

                    return (
                      <button
                        key={funnel.id}
                        type="button"
                        className={`w-full rounded-2xl border p-4 text-left transition-all duration-200 ${
                          isSelected
                            ? 'border-emerald-300 bg-emerald-50 shadow-sm ring-2 ring-emerald-100'
                            : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedFunnelId(funnel.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: funnel.cor || '#0f172a' }} />
                              <span className="truncate text-sm font-semibold text-slate-900">{funnel.nome}</span>
                            </div>
                            <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500">
                              {funnel.descricao || 'Sem descrição'}
                            </p>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            {funnel.isInbound && (
                              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                Inbound
                              </span>
                            )}
                            {isSelected && (
                              <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                                Selecionado
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                          <div className="text-[11px] text-slate-500">
                            <span className="font-semibold text-slate-800">{funnelCategoriesCount}</span> categorias
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCloneFunnel(funnel);
                              }}
                              className="font-semibold text-violet-600 hover:text-violet-700"
                            >
                              Clonar
                            </span>
                            <span
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditingFunnel(funnel);
                                setIsFormOpen(true);
                              }}
                              className="font-semibold text-blue-600 hover:text-blue-700"
                            >
                              Editar
                            </span>
                            <span
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteFunnel(funnel);
                              }}
                              className="font-semibold text-red-600 hover:text-red-700"
                            >
                              Excluir
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {funnels.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                      Nenhum funil criado ainda.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              {selectedFunnel ? (
                <div className="space-y-6 p-6 md:p-8">
                  <div className="rounded-[28px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-900 p-6 text-white">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="max-w-3xl">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="h-3.5 w-3.5 rounded-full border border-white/30" style={{ backgroundColor: selectedFunnel.cor || '#0f172a' }} />
                          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100">Funil selecionado</span>
                          {selectedFunnel.isInbound && (
                            <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-100">
                              Inbound principal
                            </span>
                          )}
                        </div>
                        <h2 className="mt-4 text-2xl font-bold md:text-3xl">{selectedFunnel.nome}</h2>
                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
                          {selectedFunnel.descricao || 'Sem descrição para este funil.'}
                        </p>
                      </div>

                      <div className="grid w-full min-w-0 grid-cols-2 gap-3 text-xs text-slate-200 sm:min-w-[260px]">
                        <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                          <div className="text-slate-400">Categorias</div>
                          <div className="mt-1 text-lg font-semibold text-white">{orderedCategories.length}</div>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
                          <div className="text-slate-400">Status</div>
                          <div className="mt-1 text-lg font-semibold text-white">{hasOrderChanges ? 'Alterado' : 'Atualizado'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => navigate(`/categorias/nova?funnelId=${selectedFunnelId}`)}
                        className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                      >
                        + Nova categoria
                      </button>
                      <button
                        onClick={() => handleCloneFunnel(selectedFunnel)}
                        className="rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20"
                      >
                        Clonar funil
                      </button>
                      <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white">
                        <input type="checkbox" checked={!!selectedFunnel.isMarketplace} disabled={isSavingMarketplaceMode} onChange={event => handleMarketplaceMode(event.target.checked)} aria-controls="funnel-marketplace-settings" className="h-4 w-4 accent-orange-500 disabled:opacity-50" />
                        Funil para marketplace
                      </label>
                      {hasOrderChanges && (
                        <button
                          onClick={handleSaveOrder}
                          className="rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400"
                        >
                          Salvar ordem
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedFunnel.isMarketplace && <div id="funnel-marketplace-settings"><FunnelPersonalizationPanel key={selectedFunnel.id} funnelId={selectedFunnel.id} categories={selectedFunnel.categories || []} /></div>}
                  <section className="rounded-3xl border border-amber-200 bg-amber-50/70 p-5 md:p-6">
                    <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">Base do agente compartilhada</h3>
                        <p className="mt-1 text-sm text-slate-600">
                          Estes dados pertencem ao funil inteiro. As categorias abaixo mudam apenas o objetivo e os exemplos de cada etapa.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveAgentBase}
                        disabled={!hasAgentBaseChanges || isSavingAgentBase}
                        className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        {isSavingAgentBase ? 'Salvando...' : 'Salvar base'}
                      </button>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="lg:col-span-2">
                        <label className="mb-1 block text-sm font-semibold text-slate-700">Nome do agente</label>
                        <input
                          value={funnelAgentBase.aiAgentName}
                          onChange={(event) => updateFunnelAgentBase('aiAgentName', event.target.value)}
                          className="input-field rounded-2xl border-slate-200 bg-white"
                          placeholder="Ex: Marcos, Carol, Ana"
                        />
                      </div>
                      {([
                        { field: 'personalidade', label: 'Personalidade', rows: 4, placeholder: 'Quem e o agente, tom de voz, jeito de falar no WhatsApp.' },
                        { field: 'regrasComunicacao', label: 'Regras de comunicacao', rows: 4, placeholder: 'Ex: max 2 frases por msg, 1 pergunta por vez, sem textao.' },
                        { field: 'contextoEmpresa', label: 'Contexto / empresa', rows: 4, placeholder: 'Sobre a empresa e o produto. Detalhes ficam na base de dados.' },
                        { field: 'escopoLimites', label: 'Escopo e limites', rows: 3, placeholder: 'Sobre o que fala, como redireciona fora do escopo.' },
                        { field: 'restricoes', label: 'O que nunca fazer', rows: 3, placeholder: 'Anti-padroes que valem para o funil todo.' },
                      ] as Array<{ field: keyof FunnelAgentBaseState; label: string; rows: number; placeholder: string }>).map(({ field, label, rows, placeholder }) => (
                        <div key={field} className={field === 'restricoes' ? 'lg:col-span-2' : ''}>
                          <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
                          <textarea
                            value={funnelAgentBase[field]}
                            onChange={(event) => updateFunnelAgentBase(field, event.target.value)}
                            rows={rows}
                            className="input-field resize-y rounded-2xl border-slate-200 bg-white"
                            placeholder={placeholder}
                          />
                        </div>
                      ))}
                    </div>
                  </section>

                  <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Kanban de categorias</h3>
                        <p className="mt-1 text-xs text-slate-500">Arraste para reordenar as etapas do funil.</p>
                      </div>
                      <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                        {orderedCategories.length} categorias
                      </div>
                    </div>

                    {orderedCategories.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                        Nenhuma categoria vinculada. Adicione uma categoria para iniciar o funil.
                      </div>
                    ) : (
                      <div className="max-h-[60vh] overflow-y-auto pr-1">
                        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                          <SortableContext items={orderedCategories.map((cat) => cat.id)} strategy={verticalListSortingStrategy}>
                            <div className="flex flex-col gap-3">
                              {orderedCategories.map((category, index) => (
                                <CategoryCard
                                  key={category.id}
                                  category={category}
                                  index={index}
                                  onEdit={(item) => navigate(`/categorias/${item.id}?funnelId=${selectedFunnelId}`)}
                                  onDelete={handleDeleteCategory}
                                />
                              ))}
                            </div>
                          </SortableContext>
                          <DragOverlay>
                            {activeCategoryId ? (
                              <div className="w-full">
                                {orderedCategories
                                  .filter((cat) => cat.id === activeCategoryId)
                                  .map((cat) => (
                                    <CategoryCard key={cat.id} category={cat} />
                                  ))}
                              </div>
                            ) : null}
                          </DragOverlay>
                        </DndContext>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[420px] items-center justify-center p-8 text-center">
                  <div className="max-w-md">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                      <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M4 12h16M4 17h10" />
                      </svg>
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-slate-900">Selecione um funil</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      Escolha um item na lateral para visualizar as etapas, editar categorias e reorganizar a ordem do processo.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <FunnelFormModal
            isOpen={isFormOpen}
            initial={editingFunnel
              ? {
                  nome: editingFunnel.nome,
                  cor: editingFunnel.cor,
                  descricao: editingFunnel.descricao || '',
                  isInbound: editingFunnel.isInbound,
                }
              : defaultFunnelForm}
            onClose={() => {
              setIsFormOpen(false);
              setEditingFunnel(null);
            }}
            onSave={handleSaveFunnel}
          />
          <AgenticFunnelWizard
            isOpen={isAgenticWizardOpen}
            isSaving={isCreatingAgenticFunnel}
            onClose={() => setIsAgenticWizardOpen(false)}
            onCreate={handleCreateAgenticFunnel}
          />
          <MarketplaceFunnelWizard isOpen={isMarketplaceWizardOpen} onClose={() => setIsMarketplaceWizardOpen(false)} onCreated={async funnel => {
            setSelectedFunnelId(funnel.id);
            setIsMarketplaceWizardOpen(false);
            await loadData(funnel.id);
          }} />
    </>
  );
}
